import React, {useCallback, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {toastIfRefused, toastStopResult} from './novaRefusalToast';
import type {InvokeResult, StopResult} from './novaInvocationStore';
import {openTaskForNova} from '../task-modal/sections/nova-thread/novaLanding';
import {isNovaInvocationEnabled} from './novaInvocationGate';
import {blockHeaderForCredits, blockHeaderForFailedTurn} from './controlState';
import {deriveEligibilityFromTask} from './eligibility';
import {NovaInvocationControl} from './NovaInvocationControl';
import {NovaPromoCard} from './NovaPromoCard';
import {useNovaAvailableAgents} from './useNovaAvailableAgents';
import {useNovaInvocationState} from './useNovaInvocationState';

export interface ConnectedNovaInvocationControlProps {
	taskId: string;
	// Threaded through to invoke() for the notification a completed run may raise — this component
	// has no other way to know the task's display name.
	taskName?: string;
	// Same reasoning as taskName — lets the notification link to the task from anywhere, not just
	// the page the run started on.
	companyProjectId?: string | number;
	companyTaskId?: string | number;
	// For the notification's project-code pill — same reasoning as companyProjectId/companyTaskId.
	projectColor?: string;
	// See InvocationControlProps.instanceKey (NovaInvocationControl.tsx) — must match whatever
	// key the agentPicker prop below was itself given.
	instanceKey?: string;
	// See InvocationControlProps.variant - the task modal's header asks for the filled treatment,
	// every task list keeps the quiet chip. Behaviour is identical either way.
	variant?: 'row' | 'header';
	readOnly: boolean;
	// From Util.isClientTaskActionsRestricted(viewer) — the caller already computes this today
	// for its own disabled/locked logic. Optional so callers with no client-role concern (or that
	// haven't been updated yet) can omit it rather than being forced to pass a hardcoded false.
	isClientActionsRestricted?: boolean;
	// Whether this person holds the edit-tasks permission. Read from the permission store at the
	// mount point rather than resolved here, so this component stays free of the app's permission
	// plumbing and testable without it. Optional and read as held when absent — a surface that has
	// not been taught to pass it keeps the behaviour it had, rather than disabling on a fact nobody
	// supplied.
	canEditTask?: boolean;
	// Opens the task for Ready, Continue or View. Rows and cards navigate to it; the task modal's
	// own header, already open, brings its thread into view instead.
	onOpenTask: () => void;
	agentPicker?: React.ReactNode;
	// From Task.hasNovaConversation — whether this task already has a conversation to continue.
	// Without it the control only knows what this browser tab has done, so a reload turns Continue
	// back into Action on a task whose conversation is still there. Optional so a mounting surface
	// that does not select the field keeps its previous behaviour rather than asserting there is
	// none.
	hasConversation?: boolean;
	// From Task.novaRunningRunId - the run the server says this task has in flight, wherever and by
	// whomever it was started. Shows Working, and is what Stop names. Optional: a surface that does
	// not select it keeps knowing only about this tab's own runs.
	runningRunId?: string | null;
	// Whether the conversation's newest turn is an error still to be retried or dismissed. Read by
	// the header variant only, which then refuses Continue until it is dealt with.
	newestTurnFailed?: boolean;
}

export const ConnectedNovaInvocationControl = (props: ConnectedNovaInvocationControlProps): JSX.Element | null => {
	const isEnabled = isNovaInvocationEnabled();
	// TaskRow.js's own memo() shallow-compares draggedNode, which changes for every visible row
	// once any row starts dragging — this component's render body runs on that same cadence
	// regardless, but memoizing here at least stops re-deriving eligibility (and re-allocating the
	// input object) on every one of those re-renders when neither underlying input actually changed.
	const eligibility = useMemo(
		() =>
			deriveEligibilityFromTask({
				readOnly: {isReadOnly: props.readOnly},
				canEditTask: props.canEditTask,
				isClientActionsRestricted: !!props.isClientActionsRestricted,
			}),
		[props.readOnly, props.canEditTask, props.isClientActionsRestricted]
	);

	// The agent list is fetched once per session (see the hook's own comment) and shared through
	// novaInvocationStore by every mounted control, not held per-row — this call site is simply
	// wherever a mounted control first happens to be, not a per-row data dependency.
	//
	// Fetched for everyone the feature is on for, including a person who cannot act. The list is not
	// only what a picker chooses from: a saved run stores an agent id, and the thread resolves that
	// id to a readable name through this same list, falling back to the raw id when it is missing.
	// Skipping the fetch here therefore does not save a person who cannot act anything — it shows
	// them an identifier where the agent's name belongs, in a thread they are meant to be able to
	// read.
	// Any eligibility reason at all, not just the permission one. A locked task is as standing as
	// a withheld permission from where the person is sitting - neither resolves from this screen -
	// so both withhold the controls rather than drawing one that can never be enabled.
	const canAct = eligibility === null;
	// Whether this control follows runs at all. A locked task still does: whoever locked it may have
	// just run Nova on it, and what that run left has to show as View rather than vanish because the
	// served hasNovaConversation predates it. Only a person who cannot act on tasks has no run of
	// their own for this to follow.
	const followsRuns = eligibility !== 'no_edit_task';
	useNovaAvailableAgents(isEnabled);

	// Hooks run unconditionally on every render — Rules of Hooks forbids skipping the call itself
	// — but passing enabled through lets the hook skip its own store subscriptions when nothing
	// they track can change what this control shows: with the flag off it renders null, and for a
	// person who cannot act it renders the same disabled pill whatever any run does.
	// accountLimitNotice isn't rendered here — the row is not its host (see NovaInvocationControl.tsx's
	// own comment); the hook still computes it for whenever a real host, like a task-detail composer,
	// exists to show it.
	const {formatMessage} = useIntl();
	// Every refusal says why, and so does every Stop.
	const onSettled = useCallback((result: InvokeResult) => toastIfRefused(result, formatMessage), [formatMessage]);
	const onStopSettled = useCallback((result: StopResult) => toastStopResult(result, formatMessage), [formatMessage]);
	const {state, specialistTitle, accountLimitNotice, creditsBlocked, creditsResetsOn, onAction, onOpenOutput, onStop} =
		useNovaInvocationState(
			props.taskId,
			eligibility,
			isEnabled && followsRuns,
			props.taskName,
			props.companyProjectId,
			props.companyTaskId,
			props.projectColor,
			props.hasConversation,
			onSettled,
			props.runningRunId,
			onStopSettled,
			props.instanceKey
		);

	const shownState =
		props.variant === 'header'
			? blockHeaderForFailedTurn(blockHeaderForCredits(state, creditsBlocked), !!props.newestTurnFailed)
			: state;

	if (!isEnabled) return null;
	// The modal header offers nothing to somebody a standing permission blocks - not even View. The
	// conversation is already open in the modal below it, so there is nothing for View to open, and
	// the header's control exists to start, steer or stop a run, none of which is theirs to do.
	if (props.variant === 'header' && (shownState.kind === 'view_only' || shownState.kind === 'hidden')) return null;

	// The card is the modal header's alone. A task row has no room for two lines of explanation
	// beside every task, and the entry banner on those screens says the same thing once instead.
	//
	// Withheld from a person who cannot act: it exists to get a run started, and promoting a
	// control they do not have is noise on a modal they opened to read.
	const promoCard =
		props.variant === 'header' && canAct ? (
			<NovaPromoCard
				state={shownState}
				specialistTitle={specialistTitle}
				creditsResetsOn={creditsResetsOn}
				accountAtLimit={accountLimitNotice === 'at_limit'}
			/>
		) : null;

	return (
		<>
			{promoCard}
			<NovaInvocationControl
				taskId={props.taskId}
				instanceKey={props.instanceKey}
				variant={props.variant}
				state={shownState}
				specialistTitle={specialistTitle}
				creditsResetsOn={creditsResetsOn}
				onAction={onAction}
				onOpenOutput={() => {
					// View marks nothing. The read state behind Ready and Continue says whether the
					// answer has been seen by somebody who can act on it, and a person who cannot
					// would otherwise clear that badge for everyone by reading the thread.
					if (shownState.kind !== 'view_only') onOpenOutput();
					// markRead() above dispatches NOVA_INVOCATION_STATE_CHANGED synchronously to every
					// subscribed control on the page (event_manager.ts has no per-listener error/queue
					// isolation), and onOpenTask() is a heavy operation of its own — a route change plus
					// the real task modal's data fetch. Running both in the same synchronous burst, on a
					// page that may host several other subscribed rows, left no breathing room between
					// "the read-state update settles" and "start opening the modal"; deferring to a
					// microtask lets this click's own re-render commit first.
					Promise.resolve().then(() => {
						// The modal's own header is already open on this task, so it has nowhere to
						// navigate and nothing to tell a modal about to open.
						if (props.variant === 'header') {
							props.onOpenTask();
							return;
						}
						// Tells the modal this opens that it was opened for Nova, and which way: View
						// only to the newest answer, Ready and Continue into Reply to Nova as well. It
						// travels with the navigation itself - see novaLanding.ts.
						openTaskForNova(shownState.kind === 'view_only' ? 'view' : 'reply', props.onOpenTask);
					});
				}}
				onStop={onStop}
				agentPicker={props.agentPicker}
			/>
		</>
	);
};
